import React, { useState, useRef, useEffect } from 'react';
import { FormField, Input, Select } from '../ui/Form';
import { Button } from '../ui/Button';
import { JournalEntryLines } from './JournalEntryLines';
import { useJournalEntry } from '../../hooks/useJournalEntry';
import { useExchangeRates } from '../../hooks/useExchangeRates';
import { JOURNAL_TYPES } from '../../config/accounting';
import { CURRENCIES, CurrencyCode } from '../../config/currency';
import { CloudinaryService } from '../../services/cloudinary/CloudinaryService';
import { Upload, X, RefreshCw, Info, Loader2, FileText, CheckCircle as CheckIcon, AlertCircle as AlertIcon, Image as ImageIcon } from 'lucide-react';
import type { JournalEntry } from '../../types/accounting';

interface NewJournalEntryFormProps {
  onClose: () => void;
  onSubmit?: (entry: Partial<JournalEntry>) => Promise<void>;
  initialEntry?: Partial<JournalEntry>;
}

export function NewJournalEntryForm({ onClose, onSubmit, initialEntry }: NewJournalEntryFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // State multi-devises
  const [transactionCurrency, setTransactionCurrency] = useState<CurrencyCode>(
    (initialEntry?.transactionCurrency as CurrencyCode) || 'CDF'
  );
  const [customExchangeRate, setCustomExchangeRate] = useState<string>(
    initialEntry?.exchangeRate?.toString() || ''
  );
  
  // Hook pour les taux de change
  const { 
    baseCurrency, 
    getSuggestedRate, 
    hasValidRate,
    calculateCDFAmount,
    isLoading: ratesLoading 
  } = useExchangeRates();

  const {
    entry,
    errors,
    totals,
    updateEntry,
    addLine,
    removeLine,
    updateLine,
    validate,
    reset,
    addAttachment,
    removeAttachment
  } = useJournalEntry(initialEntry);
  
  // Mettre à jour le taux suggéré quand la devise change
  useEffect(() => {
    if (transactionCurrency !== baseCurrency) {
      const suggestedRate = getSuggestedRate(transactionCurrency);
      if (suggestedRate > 0 && !customExchangeRate) {
        setCustomExchangeRate(suggestedRate.toString());
      }
    } else {
      setCustomExchangeRate('');
    }
  }, [transactionCurrency, baseCurrency, getSuggestedRate]);
  
  // Obtenir le taux effectif
  const effectiveExchangeRate = transactionCurrency === baseCurrency 
    ? 1 
    : (parseFloat(customExchangeRate) || getSuggestedRate(transactionCurrency));
  
  // Indicateur si la transaction est en devise étrangère
  const isForeignCurrency = transactionCurrency !== baseCurrency;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validate();
    if (!validation.isValid) return;

    setIsSubmitting(true);
    try {
      // Calculer les totaux originaux si devise étrangère
      const originalTotalDebit = isForeignCurrency 
        ? totals.totalDebit / effectiveExchangeRate 
        : undefined;
      const originalTotalCredit = isForeignCurrency 
        ? totals.totalCredit / effectiveExchangeRate 
        : undefined;
      
      const newEntry = {
        ...entry,
        totalDebit: totals.totalDebit,
        totalCredit: totals.totalCredit,
        totalVat: totals.totalVat,
        // Champs multi-devises
        baseCurrency: baseCurrency,
        transactionCurrency: isForeignCurrency ? transactionCurrency : undefined,
        exchangeRate: isForeignCurrency ? effectiveExchangeRate : undefined,
        originalTotalDebit,
        originalTotalCredit,
        lines: entry.lines?.map(line => ({
          ...line,
          id: line.id || crypto.randomUUID(),
          // Ajouter les infos devises aux lignes
          ...(isForeignCurrency && {
            currency: transactionCurrency,
            exchangeRate: effectiveExchangeRate,
            originalDebit: line.debit > 0 ? line.debit / effectiveExchangeRate : undefined,
            originalCredit: line.credit > 0 ? line.credit / effectiveExchangeRate : undefined
          })
        }))
      };

      await onSubmit?.(newEntry);
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to submit journal entry:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadError(null);

    for (const file of files) {
      const attachmentId = crypto.randomUUID();
      
      try {
        // Valider le fichier avant upload
        const validation = CloudinaryService.validateFile(file, ['image', 'document']);
        if (!validation.valid) {
          setUploadError(validation.error || `Fichier ${file.name} invalide`);
          continue;
        }

        const localUrl = URL.createObjectURL(file);
        
        addAttachment({
          id: attachmentId,
          name: file.name,
          localUrl,
          mimeType: file.type,
          size: file.size,
          status: 'pending'
        });

        if (navigator.onLine) {
          addAttachment({
            id: attachmentId,
            name: file.name,
            localUrl,
            mimeType: file.type,
            size: file.size,
            status: 'uploading'
          });

          const result = await CloudinaryService.uploadJournalAttachment(file);
          
          addAttachment({
            id: attachmentId,
            name: file.name,
            url: result.url,
            localUrl,
            mimeType: file.type,
            size: result.size,
            status: 'uploaded'
          });
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadError(`Erreur lors de l'upload de ${file.name}`);
        
        addAttachment({
          id: attachmentId,
          name: file.name,
          status: 'error'
        });
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <FormField label="Date" required>
          <Input
            type="date"
            value={entry.date}
            onChange={(e) => updateEntry({ date: e.target.value })}
            required
          />
        </FormField>

        <FormField label="Journal" required>
          <Select
            value={entry.journalType}
            onChange={(e) => updateEntry({ journalType: e.target.value })}
            required
            options={Object.entries(JOURNAL_TYPES).map(([value, config]) => ({
              value,
              label: `${config.code} - ${config.label}`
            }))}
          />
        </FormField>

        <FormField label="N° Pièce" required>
          <Input
            value={entry.reference || ''}
            onChange={(e) => updateEntry({ reference: e.target.value })}
            placeholder="Ex: FAC2024-001"
            required
          />
        </FormField>
      </div>

      {/* Section devise de transaction */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <FormField label="Devise de transaction">
          <Select
            value={transactionCurrency}
            onChange={(e) => setTransactionCurrency(e.target.value as CurrencyCode)}
            options={Object.values(CURRENCIES).map(currency => ({
              value: currency.code,
              label: `${currency.code} - ${currency.name}`
            }))}
          />
        </FormField>
        
        {isForeignCurrency && (
          <>
            <FormField 
              label={`Taux de change (1 ${transactionCurrency} = ? ${baseCurrency})`}
            >
              <div className="flex items-center space-x-2">
                <Input
                  type="number"
                  step="0.0001"
                  min="0.0001"
                  value={customExchangeRate}
                  onChange={(e) => setCustomExchangeRate(e.target.value)}
                  placeholder="Taux de change"
                  required={isForeignCurrency}
                  className="flex-1"
                />
                {ratesLoading ? (
                  <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      const suggested = getSuggestedRate(transactionCurrency);
                      if (suggested > 0) setCustomExchangeRate(suggested.toString());
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title="Utiliser le taux suggéré"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </button>
                )}
              </div>
            </FormField>
            
            <div className="flex items-center">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <Info className="h-4 w-4" />
                  <span className="font-medium">Conversion automatique</span>
                </div>
                <p className="text-blue-600/80 dark:text-blue-400/80 mt-1">
                  Les montants seront convertis en {baseCurrency} au taux de {effectiveExchangeRate.toLocaleString()}
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      <FormField label="Libellé" required>
        <Input
          value={entry.description}
          onChange={(e) => updateEntry({ description: e.target.value })}
          placeholder="Description de l'écriture"
          required
        />
      </FormField>

      <JournalEntryLines
        lines={entry.lines || []}
        onUpdate={updateLine}
        onRemove={removeLine}
        onAdd={addLine}
        totalDebit={totals.totalDebit}
        totalCredit={totals.totalCredit}
        totalVat={totals.totalVat}
      />

      {/* Section pièces jointes */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-700">Pièces justificatives</h3>
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            multiple
            accept="image/*,.pdf"
            onChange={handleFileSelect}
          />
          <Button
            type="button"
            variant="secondary"
            size="sm"
            icon={Upload}
            onClick={() => fileInputRef.current?.click()}
          >
            Ajouter un document
          </Button>
        </div>

        {uploadError && (
          <p className="text-sm text-red-600">{uploadError}</p>
        )}

        {entry.attachments && entry.attachments.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {entry.attachments.map(attachment => (
              <div 
                key={attachment.id}
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  attachment.status === 'uploading' ? 'bg-blue-50 border-blue-200' :
                  attachment.status === 'error' ? 'bg-red-50 border-red-200' :
                  attachment.status === 'uploaded' ? 'bg-green-50 border-green-200' :
                  'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-3 min-w-0 flex-1">
                  {/* Thumbnail pour images ou icône pour documents */}
                  {attachment.mimeType?.startsWith('image/') && (attachment.url || attachment.localUrl) ? (
                    <img 
                      src={attachment.url || attachment.localUrl} 
                      alt={attachment.name}
                      className="h-10 w-10 rounded object-cover border border-gray-200 flex-shrink-0"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-blue-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {attachment.name}
                    </p>
                    <div className="flex items-center gap-1.5">
                      {attachment.status === 'pending' && (
                        <span className="text-xs text-yellow-600 flex items-center gap-1">
                          <AlertIcon className="h-3 w-3" /> En attente...
                        </span>
                      )}
                      {attachment.status === 'uploading' && (
                        <span className="text-xs text-blue-600 flex items-center gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" /> Upload en cours...
                        </span>
                      )}
                      {attachment.status === 'uploaded' && (
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          <CheckIcon className="h-3 w-3" /> Téléchargé
                        </span>
                      )}
                      {attachment.status === 'error' && (
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <AlertIcon className="h-3 w-3" /> Erreur
                        </span>
                      )}
                      {attachment.size && (
                        <span className="text-xs text-gray-400">
                          · {attachment.size > 1024 * 1024 
                            ? `${(attachment.size / (1024 * 1024)).toFixed(1)} MB`
                            : `${Math.round(attachment.size / 1024)} KB`}
                        </span>
                      )}
                    </div>
                    {/* Barre de progression */}
                    {attachment.status === 'uploading' && (
                      <div className="mt-1.5 w-full bg-blue-100 rounded-full h-1.5 overflow-hidden">
                        <div className="bg-blue-500 h-1.5 rounded-full animate-[progress_2s_ease-in-out_infinite]" style={{ width: '70%' }} />
                      </div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeAttachment(attachment.id)}
                  className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-sm text-red-600">{error}</p>
          ))}
        </div>
      )}

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
          disabled={isSubmitting || errors.length > 0}
        >
          Enregistrer
        </Button>
      </div>
    </form>
  );
}